/**
 * Credit Types
 * Types for credit management, nurturing, origination, and scoring
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

export enum CreditLevel {
  BRONZE = 'BRONZE',
  SILVER = 'SILVER',
  GOLD = 'GOLD',
  PLATINUM = 'PLATINUM',
}

export enum NurturingStatus {
  NEW = 'NEW',
  EXPLORING = 'EXPLORING',
  ENGAGED = 'ENGAGED',
  READY = 'READY',
  APPLIED = 'APPLIED',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
  INACTIVE = 'INACTIVE',
}

export enum CreditStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  CURRENT = 'CURRENT',
  DELINQUENT = 'DELINQUENT',
  DEFAULT = 'DEFAULT',
  PAID_OFF = 'PAID_OFF',
  CANCELLED = 'CANCELLED',
  RESTRUCTURED = 'RESTRUCTURED',
}

export enum IncomeSourceClassification {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  PASSIVE = 'PASSIVE',
  IRREGULAR = 'IRREGULAR',
}

export enum OriginationStep {
  KYC = 'KYC',
  FINANCIAL_PROFILE = 'FINANCIAL_PROFILE',
  SCORING = 'SCORING',
  OFFER = 'OFFER',
  ACCEPTANCE = 'ACCEPTANCE',
  DISBURSEMENT = 'DISBURSEMENT',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
}

export enum OfferType {
  STANDARD = 'STANDARD',
  PROMOTIONAL = 'PROMOTIONAL',
  PRE_APPROVED = 'PRE_APPROVED',
}

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface IncomeSource {
  id: string;
  userId: string;
  source: string;
  classification: IncomeSourceClassification;
  monthlyAmount: number;
  currency: string;
  verifiedAt: string | null;
  isActive: boolean;
}

export interface FinancialProfile {
  userId: string;
  totalMonthlyIncome: number;
  totalMonthlyExpenses: number;
  debtToIncomeRatio: number;
  availableCapacity: number;
  incomeSources: IncomeSource[];
  lastUpdated: string;
}

export interface ScoreComponent {
  name: string;
  weight: number;
  rawScore: number;
  weightedScore: number;
  description: string;
}

export interface ScoreBreakdown {
  totalScore: number;
  maxScore: number;
  level: CreditLevel;
  components: ScoreComponent[];
  calculatedAt: string;
}

export interface GamificationData {
  userId: string;
  currentLevel: CreditLevel;
  currentScore: number;
  nextLevelScore: number;
  progressPercent: number;
  badges: string[];
  streakDays: number;
}

export interface CreditOffer {
  id: string;
  userId: string;
  type: OfferType;
  amount: number;
  currency: string;
  interestRate: number;
  termDays: number;
  monthlyPayment: number;
  totalPayment: number;
  expiresAt: string;
  isAccepted: boolean;
}

export interface CreditApplication {
  id: string;
  userId: string;
  offerId: string;
  currentStep: OriginationStep;
  requestedAmount: number;
  approvedAmount: number | null;
  status: CreditStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Credit {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  applicationId: string;
  amount: number;
  disbursedAmount: number;
  outstandingBalance: number;
  currency: string;
  interestRate: number;
  termDays: number;
  startDate: string;
  endDate: string;
  nextPaymentDate: string | null;
  nextPaymentAmount: number | null;
  status: CreditStatus;
  level: CreditLevel;
  daysDelinquent: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentScheduleItem {
  number: number;
  dueDate: string;
  principal: number;
  interest: number;
  totalPayment: number;
  balance: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'OVERDUE_PARTIAL' | 'PARTIAL' | 'WRITTEN_OFF';
  paidDate: string | null;
  paidAmount: number | null;
}

export interface NurturingUser {
  id: string;
  userId: string;
  email: string;
  name: string;
  status: NurturingStatus;
  currentLevel: CreditLevel;
  score: number;
  maxScore: number;
  progressPercent: number;
  lastActivity: string;
  registeredAt: string;
  daysInFunnel: number;
}

// ─── Dashboard / Stats (matches real API response from credit-engine-business) ─

/** GET /backoffice/dashboard/summary?partnerId= */
export interface CreditDashboardStats {
  total_active_credits: number;
  total_disbursed_mxn: number;
  total_applications_pending: number;
  total_active_offers: number;
  avg_credit_amount_mxn: number;
  avg_term_weeks: number;
}

/** GET /backoffice/dashboard/nurturing/funnel?partnerId= — returns array */
export interface NurturingFunnelItem {
  stage: string;
  count: number;
  conversion_rate_pct: number;
}

/** GET /backoffice/dashboard/origination/weekly?partnerId= — returns array */
export interface OriginationWeeklyItem {
  week: string;
  credits_originated: number;
  total_amount_mxn: number;
  avg_amount_mxn: number;
  avg_term_weeks: number;
}

// ─── Pagination & Filters ────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreditListFilters {
  page?: number;
  pageSize?: number;
  status?: CreditStatus;
  level?: CreditLevel;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface NurturingListFilters {
  page?: number;
  pageSize?: number;
  status?: NurturingStatus;
  level?: CreditLevel;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ─── Monthly Statements (P7) ────────────────────────────────────────────────

export interface CreditStatementListItem {
  statement_id: string;
  period: string;
  period_year: number;
  period_month: number;
  status: 'PENDING' | 'GENERATED' | 'ERROR';
  generated_at: string | null;
}

export interface CreditStatementsListResponse {
  statements: CreditStatementListItem[];
}

export interface CreditStatementPdfResponse {
  url: string;
  expires_at: string;
}

// ─── Signed Contracts (P4 / P9) ──────────────────────────────────────────────

export interface SignedContractListItem {
  contract_id: string;
  contract_type: string;
  directory_id: string;
  enterprise: string;
  folio: string;
  version: string;
  status: string;
  file_hash: string;
  created_at: string;
}

export interface SignedContractsListResponse {
  contracts: SignedContractListItem[];
}

export interface SignedContractPdfResponse {
  url: string;
  expires_at: string;
}
